import { Button, ConfigProvider, Input, Segmented, Select } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import AdminPersonnelSection from './AdminPersonnelSection';
import AdminScopeSection from './AdminScopeSection';
import AdminStudyDetailsSection from './AdminStudyDetailsSection';
import { useAdminStudyFilterOptions } from './useAdminStudyFilterOptions';
import type { StatusSegment } from './useAdminStudyFilters';
import styles from './AdminStudyFilterRail.module.css';

interface AdminStudyFilterRailProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  statusSegment: StatusSegment;
  onStatusSegmentChange: (v: StatusSegment) => void;
  isAssigned: boolean | undefined;
  onAssignedChange: (v: boolean | undefined) => void;
  dateRange: [string, string] | null;
  onDateRangeChange: (range: [string, string] | null) => void;
  selectedPriorities: string[];
  onPrioritiesChange: (v: string[]) => void;
  selectedSites: string[];
  onSitesChange: (v: string[]) => void;
  selectedReferringPhysicians: string[];
  onReferringPhysiciansChange: (v: string[]) => void;
  selectedInterpretingPhysicians: string[];
  onInterpretingPhysiciansChange: (v: string[]) => void;
  selectedSonographers: string[];
  onSonographersChange: (v: string[]) => void;
  selectedModalities: string[];
  onModalitiesChange: (v: string[]) => void;
  onExportCsv: () => void;
  isExportDisabled?: boolean;
}

const SEGMENT_OPTIONS = [
  { label: 'Unread', value: 'unread' },
  { label: 'Finalized', value: 'finalized' },
  { label: 'All', value: 'all' },
];

const ASSIGNED_OPTIONS = [
  { value: 'all', label: 'All Studies' },
  { value: 'true', label: 'Assigned' },
  { value: 'false', label: 'Unassigned' },
];

const filterTheme = {
  components: {
    Input: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    Select: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    DatePicker: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
  },
};

export default function AdminStudyFilterRail({
  searchText,
  onSearchChange,
  statusSegment,
  onStatusSegmentChange,
  isAssigned,
  onAssignedChange,
  dateRange,
  onDateRangeChange,
  selectedPriorities,
  onPrioritiesChange,
  selectedSites,
  onSitesChange,
  selectedReferringPhysicians,
  onReferringPhysiciansChange,
  selectedInterpretingPhysicians,
  onInterpretingPhysiciansChange,
  selectedSonographers,
  onSonographersChange,
  selectedModalities,
  onModalitiesChange,
  onExportCsv,
  isExportDisabled,
}: AdminStudyFilterRailProps) {
  const {
    siteOptions,
    modalityOptions,
    priorityOptions,
    referringOptions,
    physicianOptions,
    sonographerOptions,
  } = useAdminStudyFilterOptions();

  const handleAssignedChange = (val: string) => {
    onAssignedChange(val === 'all' ? undefined : val === 'true');
  };

  return (
    <ConfigProvider theme={filterTheme}>
      <div className={styles.filterRail}>
        <div className={styles.section}>
          <div className={styles.filterGroup}>
            <label htmlFor="admin-study-search" className={styles.filterLabel}>
              Search
            </label>
            <Input
              id="admin-study-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search patient, MRN..."
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Status</span>
            <Segmented
              block
              size="small"
              options={SEGMENT_OPTIONS}
              value={statusSegment}
              onChange={(v) => onStatusSegmentChange(v as StatusSegment)}
            />
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="admin-assigned" className={styles.filterLabel}>
              Assigned
            </label>
            <Select
              id="admin-assigned"
              value={isAssigned === undefined ? 'all' : String(isAssigned)}
              onChange={handleAssignedChange}
              options={ASSIGNED_OPTIONS}
              className={styles.fullWidth}
            />
          </div>
        </div>

        <AdminStudyDetailsSection
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          selectedPriorities={selectedPriorities}
          onPrioritiesChange={onPrioritiesChange}
          selectedModalities={selectedModalities}
          onModalitiesChange={onModalitiesChange}
          priorityOptions={priorityOptions}
          modalityOptions={modalityOptions}
        />

        <AdminScopeSection
          selectedSites={selectedSites}
          onSitesChange={onSitesChange}
          siteOptions={siteOptions}
        />

        <AdminPersonnelSection
          selectedReferringPhysicians={selectedReferringPhysicians}
          onReferringPhysiciansChange={onReferringPhysiciansChange}
          selectedInterpretingPhysicians={selectedInterpretingPhysicians}
          onInterpretingPhysiciansChange={onInterpretingPhysiciansChange}
          selectedSonographers={selectedSonographers}
          onSonographersChange={onSonographersChange}
          referringOptions={referringOptions}
          physicianOptions={physicianOptions}
          sonographerOptions={sonographerOptions}
        />

        <div className={styles.actions}>
          <span className={styles.filterLabel}>Actions</span>
          <Button
            block
            icon={<DownloadOutlined />}
            onClick={onExportCsv}
            disabled={isExportDisabled}
          >
            Export CSV
          </Button>
        </div>
      </div>
    </ConfigProvider>
  );
}
